import {
    Box,
    Button,
    ButtonGroup,
    Card,
    CardContent,
    FormControl,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography,
  } from "@mui/material";
  import React, { useEffect, useRef, useState } from "react";
  import MainNavBar from "../../../layout/pages/navbar/MainNavBar";
  import LayoutHeading from "../../../layout/components/layout-heading/LayoutHeading";
  import * as productActions from "../../../../redux/products/products.actions";
  import * as productReducers from "../../../../redux/products/products.reducers";
  import * as categoryActions from "../../../../redux/categories/categories.actions";
  import * as categoryReducers from "../../../../redux/categories/categories.reducers";
  import {
    AppDispatch,
    RootState,
    useAppDispatch,
  } from "../../../../redux/store";
  import { useNavigate, useParams } from "react-router-dom";
  import { useSelector } from "react-redux";
  import SpinnerUI from "../../../ui/components/spinner/SpinnerUI";
  import * as Yup from "yup";
  import { useFormik } from "formik";
  import {
    ICategoryResponseView,
    ISubCategoryView,
  } from "../../../categories/models/ICategoryResponseView";
  import { UploadImageWidget } from "../../../../utils/UploadImageWidget";
  
  const validationSchema = Yup.object().shape({
    categoryId: Yup.string().required("Category is required"),
    subCategoryId: Yup.string().required("Sub Category is required"),
    title: Yup.string().trim().required("Product Name is required"),
    description: Yup.string().trim().required("Description is required"),
    imageUrl: Yup.string().trim().required("Image URL is required"),
    brand: Yup.string().trim().required("Brand is required"),
    price: Yup.number().required("Price is required"),
    quantity: Yup.number().required("Quantity is required"),
  });
  
  const EditUser: React.FC = () => {
    const dispatch: AppDispatch = useAppDispatch();
    const navigate = useNavigate();
    const { productId } = useParams();
    const [categoryId, setCategoryId] = useState<string>("");
    const [categoryName, setCategoryName] = useState<string>("fashion");
    const [subCategoryId, setSubCategoryId] = useState<string>("");
    const [imageUrl, setImageUrl] = useState<string>("");
    const [subCategories, setSubCategories] = useState<ISubCategoryView[]>([]);
    const cloudinaryRef = useRef<any>();
    const widgetRef = useRef<any>();
  
    //Get Products Data from Redux Store
    const productsReduxState: productReducers.InitialState = useSelector(
      (state: RootState) => {
        return state[productReducers.productFeatureKey];
      }
    );
  
    const { loading, product } = productsReduxState;
  
    useEffect(() => {
      if (productId) {
        dispatch(productActions.getProductAction({ productId: productId }));
      }
    }, [productId]);
  
    const handleUploadImage = () => {
      widgetRef.current.open();
    };
  
    // Get all Categories from redux
    const categoryReduxState: categoryReducers.InitialState = useSelector(
      (state: RootState) => state[categoryReducers.categoryFeatureKey]
    );
  
    const { loading: categoryLoading, categories } = categoryReduxState;
  
    useEffect(() => {
      dispatch(categoryActions.getAllCategoriesAction());
      UploadImageWidget.upload(cloudinaryRef, widgetRef)
        .then((imageUrl) => {
          if (imageUrl) {
            setImageUrl(imageUrl.toString());
            formik.setFieldValue("imageUrl", imageUrl.toString());
          }
        })
        .catch((err) => {
          console.log(err);
        });
    }, [dispatch]);
  
    const handleCategoryChange = (e: any) => {
      const selectedCategoryId = e.target.value;
      setCategoryId(selectedCategoryId);
  
      const selectedCategory = categories.find(
        (category) => category._id === selectedCategoryId
      );
      setCategoryName(selectedCategory?.name || "");
  
      formik.setFieldValue("categoryId", selectedCategoryId);
  
      if (categories.length > 0) {
        let category: ICategoryResponseView | undefined = categories.find(
          (category) => category?._id?.toString() === selectedCategoryId
        );
        if (category && category.subCategories) {
          setSubCategories(category.subCategories);
        } else {
          setSubCategories([] as ISubCategoryView[]);
        }
      }
    };
  
    const handleSubCategoryChange = (e: any) => {
      const selectedSubCategoryId = e.target.value;
      setSubCategoryId(selectedSubCategoryId);
      formik.setFieldValue("subCategoryId", selectedSubCategoryId);
    };
  
    const handleUpdateProduct = async (values: any) => {
      if (productId) {
        const response = await dispatch(
          productActions.udpateProductAction({
            product: values,
            productId: productId,
          })
        );
  
        if (response && !response.error) {
          navigate(`/products/${categoryName.toLowerCase()}`);
        }
      }
    };
  
    const formik = useFormik({
      initialValues: {
        title: product.title,
        description: product.description,
        imageUrl: product.imageUrl,
        brand: product.brand,
        price: product.price,
        quantity: product.quantity,
        categoryId: product?.categoryObj?._id,
        subCategoryId: product?.subCategoryObj?._id,
      },
      validationSchema: validationSchema,
      onSubmit: async (values) => {
        await handleUpdateProduct(values);
      },
    });
  
    return (
      <>
        <Box>
          {(loading || categoryLoading) && <SpinnerUI />}
          <MainNavBar />
          <LayoutHeading heading="Edit User" description="Efficiently update user profiles, permissions, and details with ease and accuracy." />
          <Grid
            container
            spacing={2}
            sx={{ display: "flex", justifyContent: "center" }}
          >
            <Grid item xs={10} md={4}>
              <form onSubmit={formik.handleSubmit}>
                <FormControl fullWidth>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="inherit"
                    sx={{ mb: 2, textTransform: "capitalize" }}
                    onClick={() => handleUploadImage()}
                  >
                    Upload Image
                  </Button>
                  {formik.touched.imageUrl && formik.errors.imageUrl && (
                    <FormHelperText error>
                      {formik.errors.imageUrl}
                    </FormHelperText>
                  )}
                </FormControl>
                <FormControl
                  variant="outlined"
                  fullWidth
                  sx={{ minWidth: 120, mb: 2 }}
                  size="small"
                  error={
                    formik.touched.categoryId && Boolean(formik.errors.categoryId)
                  }
                >
                  <InputLabel id="demo-select-small-label">Category</InputLabel>
                  <Select
                    labelId="demo-select-small-label"
                    id="demo-select-small"
                    value={categoryId}
                    label="Category"
                    onChange={handleCategoryChange}
                  >
                    <MenuItem value="">
                      <em>Select Category</em>
                    </MenuItem>
                    {categories.map((category, index) => (
                      <MenuItem key={index} value={category._id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.categoryId && formik.errors.categoryId && (
                    <FormHelperText>{formik.errors.categoryId}</FormHelperText>
                  )}
                </FormControl>
                <FormControl
                  variant="outlined"
                  fullWidth
                  sx={{ minWidth: 120, mb: 2 }}
                  size="small"
                  error={
                    formik.touched.subCategoryId &&
                    Boolean(formik.errors.subCategoryId)
                  }
                >
                  <InputLabel id="demo-select-small-label">
                    Sub Category
                  </InputLabel>
                  <Select
                    labelId="demo-select-small-label"
                    id="demo-select-small"
                    value={subCategoryId}
                    label="Sub Category"
                    onChange={handleSubCategoryChange}
                  >
                    <MenuItem value="">
                      <em>Select Sub Category</em>
                    </MenuItem>
                    {subCategories.map((subCategory, index) => (
                      <MenuItem key={index} value={subCategory._id}>
                        {subCategory.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.subCategoryId &&
                    formik.errors.subCategoryId && (
                      <FormHelperText>
                        {formik.errors.subCategoryId}
                      </FormHelperText>
                    )}
                </FormControl>
                <TextField
                  sx={{ mb: 2 }}
                  type="text"
                  placeholder="Product Title"
                  size="small"
                  label="Product Title"
                  variant="outlined"
                  {...formik.getFieldProps("title")}
                  error={formik.touched.title && Boolean(formik.errors.title)}
                  helperText={formik.touched.title && formik.errors.title}
                  fullWidth
                />
                <TextField
                  sx={{ mb: 2 }}
                  type="text"
                  placeholder="Brand"
                  size="small"
                  label="Brand"
                  variant="outlined"
                  {...formik.getFieldProps("brand")}
                  error={formik.touched.brand && Boolean(formik.errors.brand)}
                  helperText={formik.touched.brand && formik.errors.brand}
                  fullWidth
                />
                <TextField
                  sx={{ mb: 2 }}
                  id="standard-textarea"
                  multiline
                  rows={2}
                  label="Description"
                  placeholder="Description"
                  variant="outlined"
                  {...formik.getFieldProps("description")}
                  error={
                    formik.touched.description &&
                    Boolean(formik.errors.description)
                  }
                  helperText={
                    formik.touched.description && formik.errors.description
                  }
                  fullWidth
                  size="small"
                />
                <TextField
                  sx={{ mb: 2 }}
                  type="number"
                  placeholder="Price"
                  size="small"
                  label="Price"
                  variant="outlined"
                  {...formik.getFieldProps("price")}
                  error={formik.touched.price && Boolean(formik.errors.price)}
                  helperText={formik.touched.price && formik.errors.price}
                  fullWidth
                />
                <TextField
                  sx={{ mb: 2 }}
                  type="number"
                  placeholder="Quantity"
                  size="small"
                  label="Quantity"
                  variant="outlined"
                  {...formik.getFieldProps("quantity")}
                  error={
                    formik.touched.quantity && Boolean(formik.errors.quantity)
                  }
                  helperText={formik.touched.quantity && formik.errors.quantity}
                  fullWidth
                />
                <ButtonGroup fullWidth>
                  <Button
                    variant="contained"
                    type="submit"
                    sx={{ textTransform: "capitalize" }}
                    fullWidth
                  >
                    Update Product
                  </Button>
                  <Button
                    variant="contained"
                    sx={{ textTransform: "capitalize" }}
                    fullWidth
                    color="warning"
                    onClick={() => navigate('/products/admin')}
                  >
                    Cancel
                  </Button>
                </ButtonGroup>
              </form>
            </Grid>
            {/* This Grid is used to show available subcategories in a specific category */}
            <Grid item xs={10} md={6}>
              <Card>
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      textAlign: "center",
                      background: "#000048",
                      color: "white",
                      fontFamily: "Ubuntu",
                      py: 1,
                      fontSize: "16px",
                      letterSpacing: "1px",
                    }}
                  >
                    Product Image Preview
                  </Typography>
                </Box>
                <CardContent sx={{ display: "flex", justifyContent: "center" }}>
                  {imageUrl || product.imageUrl && (
                    <Box
                      component={Paper}
                      elevation={15}
                      sx={{
                        width: "500px",
                        height: "430px",
                        m: 3,
                        borderRadius: "10px",
                      }}
                    >
                      <img
                        src={product.imageUrl || imageUrl}
                        alt="No Image Found"
                        style={{
                          width: "100%",
                          height: "100%",
                          borderRadius: "10px",
                          border: "1px solid white",
                        }}
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </>
    );
  };
  
  export default EditUser;
  